import React from "react";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  content: {
    fontSize: "14.5px",
    lineHeight: 1.5,
    fontFamily: "'IBM Plex Sans', sans-serif",
    color: "#374151",
    wordBreak: "break-word",
    textAlign: "justify",
    "& p": {
      margin: "0 0 6px 0",
      "&:last-child": { margin: 0 },
    },
    "& strong": {
      color: "#111827",
      fontWeight: 600,
    },
    "& em": {
      fontStyle: "italic",
    },
    "& h2": { fontSize: "17px", fontWeight: 600, color: "#111827", margin: "16px 0 6px 0", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.35 },
    "& h3": { fontSize: "15.5px", fontWeight: 600, color: "#111827", margin: "14px 0 6px 0", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.35 },
    "& h4": { fontSize: "14.5px", fontWeight: 600, color: "#111827", margin: "12px 0 4px 0", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.35 },
    "& h2:first-child, & h3:first-child, & h4:first-child": { marginTop: 0 },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      margin: "16px 0",
      fontSize: "14px",
    },
    "& th": {
      background: "#1e3a5f",
      color: "#fff",
      padding: "10px 14px",
      textAlign: "left",
      fontWeight: 600,
      fontSize: "13px",
    },
    "& th:first-child": { borderRadius: "6px 0 0 0" },
    "& th:last-child": { borderRadius: "0 6px 0 0" },
    "& td": {
      padding: "10px 14px",
      borderBottom: "1px solid #e5e7eb",
      color: "#374151",
    },
    "& tr:last-child td": { borderBottom: "none" },
    "& tr:nth-child(even)": { background: "#fafbfc" },
    "& ul, & ol": {
      paddingLeft: "20px",
      margin: "4px 0",
    },
    "& li": {
      marginBottom: "1px",
      lineHeight: 1.5,
    },
    "& br.pgap": {
      display: "block",
      marginBottom: "4px",
    },
    "& code": {
      background: "#f3f4f6",
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "13px",
      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
      color: "#1e3a5f",
    },
    "& pre": {
      background: "#1e1e1e",
      color: "#d4d4d4",
      padding: "16px",
      borderRadius: "8px",
      overflowX: "auto",
      margin: "16px 0",
      fontSize: "13px",
      lineHeight: 1.6,
      "& code": {
        background: "none",
        padding: 0,
        color: "inherit",
        fontSize: "13px",
      },
    },
    "& a": {
      color: "#2563eb",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
}));

const SAFE_TAGS = new Set([
  "strong", "em", "h2", "h3", "h4",
  "table", "thead", "tbody", "tr", "th", "td",
  "ul", "ol", "li", "pre", "code", "br", "p", "a",
]);

function sanitizeHTML(html) {
  // Remove script, iframe, object, embed tags entirely
  let clean = html.replace(/<(script|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, "");
  clean = clean.replace(/<(script|iframe|object|embed)[^>]*\/?>/gi, "");

  // Remove event handlers (on*)
  clean = clean.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  // Remove javascript: hrefs
  clean = clean.replace(/href\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, "");

  // Strip tags not in whitelist — keep their content
  clean = clean.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/g, function (match, tag) {
    const lower = tag.toLowerCase();
    if (SAFE_TAGS.has(lower)) {
      // For <a> tags, only allow href with http/https
      if (lower === "a") {
        // Extract href if present
        var hrefMatch = match.match(/href\s*=\s*"(https?:\/\/[^"]*)"/i);
        if (hrefMatch) {
          if (match.startsWith("</")) return "</a>";
          return '<a href="' + hrefMatch[1] + '" target="_blank" rel="noopener noreferrer">';
        }
        // No valid href, strip the tag but keep content
        if (match.startsWith("</")) return "";
        return "";
      }
      return match;
    }
    return "";
  });

  return clean;
}

function stripRiskLabels(text) {
  if (!text) return text;
  // Remove lines that are just risk labels like "⚠ HIGH RISK: ..." or "⚠️ MEDIUM RISK: ..."
  var result = text.replace(/[⚠️⚠]\s*(?:HIGH|MEDIUM|LOW)\s*RISK\s*:\s*/gi, "");
  // Also remove standalone risk header lines
  result = result.replace(/^\s*#+\s*(?:HIGH|MEDIUM|LOW)\s*RISK\s*$/gim, "");
  // Clean up any leftover double blank lines from removed content
  result = result.replace(/\n{3,}/g, "\n\n");
  return result;
}

function stripMarkdownTables(text) {
  return text.replace(/(?:^|\n)(\|.+\|)\n(\|[-| :]+\|)\n((?:\|.+\|\n?)+)/g, "").trim();
}

function stripFollowUps(content, followUps) {
  if (!followUps || !followUps.length || !content) return content;

  var cutIndex = content.length;
  for (var i = 0; i < followUps.length; i++) {
    var fu = followUps[i];
    if (!fu) continue;
    var idx = content.indexOf(fu);
    if (idx !== -1 && idx < cutIndex) cutIndex = idx;
  }

  var result;
  if (cutIndex < content.length) {
    result = content.slice(0, cutIndex);
    result = result.replace(/\n+[-*\d.]+\s*$/, "");
    result = result.replace(/\n+\*?\*?[^\n]{0,60}(?:follow|suggest|consider)[^\n]*\*?\*?\s*$/i, "");
  } else {
    result = content.replace(
      /\n{1,2}\*?\*?\s*(?:follow[- ]?up|suggested|next)\s+(?:questions?|steps?)[^\n]*[\s\S]*$/gi,
      ""
    );
  }

  return result.trim();
}

function parseMarkdown(text) {
  if (!text) return "";
  var html = text;
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, "<pre><code>$2</code></pre>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  html = html.replace(/^### (.+)$/gm, "<h4>$1</h4>");
  html = html.replace(/^## (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^# (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(
    /(?:^|\n)(\|.+\|)\n(\|[-| :]+\|)\n((?:\|.+\|\n?)+)/g,
    function (match, header, separator, body) {
      var headers = header
        .split("|")
        .filter(function (c) { return c.trim(); })
        .map(function (c) { return "<th>" + c.trim() + "</th>"; })
        .join("");
      var rows = body
        .trim()
        .split("\n")
        .map(function (row) {
          var cells = row
            .split("|")
            .filter(function (c) { return c.trim(); })
            .map(function (c) { return "<td>" + c.trim() + "</td>"; })
            .join("");
          return "<tr>" + cells + "</tr>";
        })
        .join("");
      return "<table><thead><tr>" + headers + "</tr></thead><tbody>" + rows + "</tbody></table>";
    }
  );
  html = html.replace(/^- (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>.*<\/li>\n?)+/g, "<ul>$&</ul>");
  html = html.replace(/^\d+\. (.+)$/gm, "<oli>$1</oli>");
  html = html.replace(/(<oli>.*<\/oli>\n?)+/g, function (match) {
    return "<ol>" + match.replace(/<\/?oli>/g, function (t) { return t.replace("oli", "li"); }) + "</ol>";
  });
  html = html.replace(/\n\n/g, '<br class="pgap"/>');
  html = html.replace(/\n/g, "<br/>");
  html = html.replace(/<pre>([\s\S]*?)<\/pre>/g, function (match, content) {
    return "<pre>" + content.replace(/<br[^>]*\/?>/g, "\n") + "</pre>";
  });
  html = html.replace(/<table>([\s\S]*?)<\/table>/g, function (match, content) {
    return "<table>" + content.replace(/<br[^>]*\/?>/g, "") + "</table>";
  });
  return html;
}

const MarkdownRenderer = React.memo(function MarkdownRenderer({ content, followUps, stripTable }) {
  var classes = useStyles();

  if (!content) return null;

  var processed = stripFollowUps(content, followUps);
  if (stripTable) {
    processed = stripMarkdownTables(processed);
  }
  processed = stripRiskLabels(processed);
  var html = parseMarkdown(processed);
  html = sanitizeHTML(html);

  return (
    <div
      className={classes.content}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

export default MarkdownRenderer;
